import { useState } from 'react';
import { PremiumBadge } from '@/design-system/primitives/premium';
import { AppIcon } from '@/modules/shared/components/ui/AppIcon';
import { AlertTriangle, AlertCircle, Info, CheckCircle2, ChevronDown, ChevronUp } from 'lucide-react';
import type { GuidanceItem, GuidanceSeverity } from '../../types/guidance';
import { sortGuidanceItems } from '../../types/guidance';

interface TabGuidancePanelProps {
  items: GuidanceItem[];
  /** Shown when there is nothing pending. Defaults to a generic up-to-date message. */
  emptyMessage?: string;
  maxPrimary?: number;
}

const SEVERITY_META: Record<GuidanceSeverity, {
  label: string;
  icon: typeof AlertTriangle;
  variant: 'error' | 'warning' | 'info';
  wrapper: string;
}> = {
  blocking: { label: 'Blocking', icon: AlertTriangle, variant: 'error', wrapper: 'border-red-200 bg-red-50/40 dark:border-red-800/50 dark:bg-red-950/20' },
  warning: { label: 'Warning', icon: AlertCircle, variant: 'warning', wrapper: 'border-amber-200 bg-amber-50/40 dark:border-amber-800/50 dark:bg-amber-950/20' },
  info: { label: 'Info', icon: Info, variant: 'info', wrapper: 'border-sky-200 bg-sky-50/40 dark:border-sky-800/50 dark:bg-sky-950/20' },
};

function GuidanceActionControl({ action }: { action: GuidanceItem['primaryAction'] | GuidanceItem['secondaryAction'] }) {
  if (!action) return null;
  if (action.disabledReason) {
    return <span className="shrink-0 text-[11px] text-muted-foreground italic">{action.disabledReason}</span>;
  }
  return (
    <button
      type="button"
      onClick={action.onClick}
      className="shrink-0 text-[11px] font-semibold text-primary hover:underline"
    >
      {action.label}
    </button>
  );
}

function GuidanceRow({ item }: { item: GuidanceItem }) {
  const meta = SEVERITY_META[item.severity];
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <div className="flex min-w-0 items-center gap-2.5">
        <PremiumBadge size="sm" variant={meta.variant}>{meta.label}</PremiumBadge>
        <div className="min-w-0">
          <p className="text-[11px] font-medium text-foreground truncate">{item.issue}</p>
          {item.detail && <p className="text-[11px] text-muted-foreground truncate">{item.detail}</p>}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-3">
        <GuidanceActionControl action={item.secondaryAction} />
        <GuidanceActionControl action={item.primaryAction} />
      </div>
    </div>
  );
}

/**
 * Compact "Next Step / Guidance" panel embedded at the top of a Patient360
 * tab. Every tab derives its own GuidanceItem[] from data/permissions it
 * already has — this component owns only presentation, priority ordering,
 * capping, and the healthy empty state.
 */
export function TabGuidancePanel({ items, emptyMessage, maxPrimary = 3 }: TabGuidancePanelProps) {
  const [expanded, setExpanded] = useState(false);
  const sorted = sortGuidanceItems(items);

  if (sorted.length === 0) {
    return (
      <section
        className="w-full rounded-md border border-emerald-200 bg-emerald-50/40 dark:border-emerald-800/50 dark:bg-emerald-950/20 px-3 py-2"
        role="region"
        aria-label="Guidance"
      >
        <div className="flex items-center gap-2">
          <AppIcon icon={CheckCircle2} size="compact" variant="success" />
          <p className="text-[11px] font-medium text-foreground">
            {emptyMessage ?? 'No immediate action required — this area is up to date.'}
          </p>
        </div>
      </section>
    );
  }

  const primary = sorted.slice(0, maxPrimary);
  const rest = sorted.slice(maxPrimary);
  const topSeverity = sorted[0].severity;
  const meta = SEVERITY_META[topSeverity];

  return (
    <section
      className={`w-full rounded-md border ${meta.wrapper}`}
      role="region"
      aria-label="Guidance"
    >
      <div className="flex items-center gap-2 px-3 pt-2.5">
        <AppIcon icon={meta.icon} size="compact" variant={meta.variant} />
        <h2 className="text-[12px] font-semibold text-foreground">Next Step{sorted.length > 1 ? `s (${sorted.length})` : ''}</h2>
      </div>
      <div className="divide-y divide-border/60 px-3 py-1.5">
        {primary.map((item) => <GuidanceRow key={item.id} item={item} />)}
        {expanded && rest.map((item) => <GuidanceRow key={item.id} item={item} />)}
      </div>
      {rest.length > 0 && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex w-full items-center justify-center gap-1 border-t border-border/60 py-1 text-[11px] font-medium text-muted-foreground hover:text-foreground"
        >
          {expanded ? <>Show less <ChevronUp className="h-3 w-3" /></> : <>View all actions ({rest.length} more) <ChevronDown className="h-3 w-3" /></>}
        </button>
      )}
    </section>
  );
}
